(() => {
  if (typeof window.customElements.get('custom-watch-showcase') !== 'undefined') {
    return;
  }

  const COMPONENT_KEYS = ['band', 'case', 'buckle', 'face', 'second'];
  // Public Fluid Configure API key used by Nixon's storefront Fluid client
  const FLUID_API_KEY = 'Nixon-2DM4k1ngL1ttl3Th1ngsB3tt3r';
  const RECIPE_API_BASE = 'https://cdn-prod.fluidconfigure.com/api/recipe';

  const formatRecipePrice = (rawPrice, currency) => {
    if (rawPrice == null || rawPrice === '') return '';

    const amount = Number(rawPrice);
    if (Number.isNaN(amount)) return String(rawPrice);

    const currencyCode = currency || 'USD';

    try {
      let formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);

      if (currencyCode === 'AUD' || currencyCode === 'CAD' || currencyCode === 'USD') {
        formatted = formatted
          .replace(/[A-Z]{1,2}\s*\$/g, '$')
          .replace(/\$\s*[A-Z]{2}\b/g, '$');
      }

      return formatted;
    } catch (e) {
      return `$${amount}`;
    }
  };

  const variantPriceCents = (variant) => {
    const cents = Number(variant?.price_cents);
    if (Number.isFinite(cents)) return cents;

    const dollars = Number(String(variant?.price || '').replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(dollars)) return 0;
    return Math.round(dollars * 100);
  };

  const getComponentSkus = (recipe) => {
    const componentsData = recipe?.extended_attributes || {};
    return COMPONENT_KEYS.reduce((obj, key) => {
      const vendorId = componentsData[key]?.value?.info?.vendorId;
      if (vendorId) obj[key] = String(vendorId);
      return obj;
    }, {});
  };

  class CustomWatchShowcase extends HTMLElement {
    constructor() {
      super();

      this.cards = [];
      this.recipeCache = new Map();
      this.componentsCache = new Map();
      this.currency = this.dataset.currency || 'USD';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.strings = {
        adding: this.dataset.stringAdding || 'Adding...',
        addToCart: this.dataset.stringAddToCart || 'Add to cart',
        unavailable: this.dataset.stringUnavailable || 'Unavailable',
      };

      this.onAtcClick = this.onAtcClick.bind(this);
    }

    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-showcase-card]')).map((el) =>
        this.buildCard(el)
      );

      if (!this.cards.length) return;

      this.cards.forEach((card) => {
        if (card.atcButton) {
          card.atcButton.addEventListener('click', this.onAtcClick);
        }
      });

      this.init().catch((error) => {
        console.error('[custom-watch-showcase]', error);
        this.cards.forEach((card) => this.showFallbackPrice(card));
      });
    }

    disconnectedCallback() {
      this.cards.forEach((card) => {
        card.atcButton?.removeEventListener('click', this.onAtcClick);
      });
    }

    buildCard(el) {
      return {
        el,
        recipeId: (el.dataset.recipeId || '').trim(),
        configureId: (el.dataset.configureId || '').trim(),
        variantId: el.dataset.variantId,
        variantPrice: Number(el.dataset.variantPrice || 0),
        productUrl: el.dataset.productUrl,
        componentsCollection: el.dataset.componentsCollection || '',
        priceEl: el.querySelector('[data-showcase-price]'),
        imageEl: el.querySelector('.custom-watch-showcase__product-image'),
        mediaLink: el.querySelector('.custom-watch-showcase__media-link'),
        atcButton: el.querySelector('button[data-showcase-atc]'),
        componentVariants: null,
        recipeData: null,
      };
    }

    showFallbackPrice(card) {
      if (!card.priceEl) return;
      const fallback = card.priceEl.dataset.fallbackPrice || '';
      card.priceEl.textContent = fallback;
      card.priceEl.classList.remove('is-loading');
      card.el.classList.remove('is-loading-recipe');
    }

    setCardImage(card, imageUrl) {
      if (!imageUrl) return;

      let img = card.imageEl;
      if (!img && card.mediaLink) {
        img = document.createElement('img');
        img.className = 'custom-watch-showcase__product-image';
        img.alt = card.el.querySelector('.custom-watch-showcase__title')?.textContent?.trim() || '';
        img.loading = 'lazy';
        card.mediaLink.appendChild(img);
        card.imageEl = img;
      }

      if (!img) return;

      img.src = imageUrl;
      img.removeAttribute('srcset');
      img.classList.add('is-recipe-image');
    }

    async fetchRecipe(recipeId) {
      const id = String(recipeId);
      if (this.recipeCache.has(id)) {
        return this.recipeCache.get(id);
      }

      const response = await fetch(
        `${RECIPE_API_BASE}/${id}?apiKey=${encodeURIComponent(FLUID_API_KEY)}&include=extended_attributes`
      );
      if (!response.ok) {
        throw new Error(`Recipe fetch failed (${response.status})`);
      }

      const data = await response.json();
      this.recipeCache.set(id, data);
      return data;
    }

    setCardPrice(card) {
      if (!card.priceEl) return;

      const cartCents = this.computeCartPriceCents(card);
      if (cartCents != null) {
        const formattedCartPrice = formatRecipePrice(cartCents / 100, this.currency);
        if (formattedCartPrice) {
          card.priceEl.textContent = formattedCartPrice;
          card.priceEl.classList.remove('is-loading');
          return;
        }
      }

      const priceRaw =
        card.recipeData?.prices?.[this.currency] ||
        card.recipeData?.prices?.USD ||
        card.recipeData?.custom?.price ||
        '';
      const formatted = formatRecipePrice(priceRaw, this.currency);
      if (formatted) {
        card.priceEl.textContent = formatted;
        card.priceEl.classList.remove('is-loading');
        return;
      }

      this.showFallbackPrice(card);
    }

    computeCartPriceCents(card) {
      const parentCents = Number(card.variantPrice);
      if (!Number.isFinite(parentCents)) return null;

      const componentSkus = getComponentSkus(card.recipeData);
      const skuList = Object.values(componentSkus);
      if (!skuList.length || !Array.isArray(card.componentVariants)) return null;

      const variantsInBundle = card.componentVariants.filter((variant) =>
        skuList.includes(String(variant.sku))
      );
      if (variantsInBundle.length !== skuList.length) return null;

      const componentCents = variantsInBundle.reduce(
        (sum, variant) => sum + variantPriceCents(variant),
        0
      );
      return parentCents + componentCents;
    }

    async init() {
      const recipeCards = this.cards.filter((card) => card.recipeId);

      await Promise.all(
        recipeCards.map(async (card) => {
          card.el.classList.add('is-loading-recipe');
          card.priceEl?.classList.add('is-loading');

          try {
            const recipe = await this.fetchRecipe(card.recipeId);
            card.recipeData = recipe;

            const imageUrl =
              recipe?.images?.view_1 ||
              recipe?.images?.View_1 ||
              (recipe?.images && Object.values(recipe.images)[0]) ||
              '';
            if (imageUrl) {
              this.setCardImage(card, imageUrl);
            }

            await this.prefetchComponents(card);
            this.setCardPrice(card);
          } catch (error) {
            console.error('[custom-watch-showcase] Recipe load failed:', error);
            this.showFallbackPrice(card);
          } finally {
            card.el.classList.remove('is-loading-recipe');
            card.priceEl?.classList.remove('is-loading');
          }
        })
      );

      this.cards.forEach((card) => {
        if (!card.recipeId) this.showFallbackPrice(card);
      });
    }

    async prefetchComponents(card) {
      if (!card.componentsCollection) return;
      try {
        card.componentVariants = await this.fetchComponentVariants(card.componentsCollection);
      } catch (error) {
        console.warn('[custom-watch-showcase] Unable to fetch components:', error);
        card.componentVariants = null;
      }
    }

    async fetchComponentVariants(handle) {
      if (!handle) return [];
      if (this.componentsCache.has(handle)) {
        return this.componentsCache.get(handle);
      }

      const request = fetch(`/collections/${handle}?view=custom-feed-json`)
        .then(async (response) => {
          if (!response.ok) throw new Error(`Components fetch failed (${response.status})`);
          const data = await response.json();
          return data?.products || [];
        })
        .catch((error) => {
          this.componentsCache.delete(handle);
          throw error;
        });

      this.componentsCache.set(handle, request);
      return request;
    }

    openSideCart(response) {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (!cart) {
        window.location.href = this.cartUrl;
        return;
      }

      cart.classList.remove('is-empty');
      if (typeof cart.renderContents === 'function' && response?.sections) {
        cart.setActiveElement?.(document.activeElement);
        cart.renderContents(response);
        return;
      }

      cart.open?.();
    }

    async onAtcClick(event) {
      event.preventDefault();

      const button = event.currentTarget;
      const cardEl = button.closest('[data-showcase-card]');
      const card = this.cards.find((item) => item.el === cardEl);
      if (!card || button.disabled) return;

      if (!card.variantId) {
        button.textContent = this.strings.unavailable;
        setTimeout(() => {
          button.textContent = this.strings.addToCart;
          button.disabled = false;
        }, 2500);
        return;
      }

      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = this.strings.adding;

      try {
        const response = card.recipeId
          ? await this.addCardToCart(card)
          : await this.addSimpleToCart(card);
        this.openSideCart(response);
        button.textContent = originalLabel || this.strings.addToCart;
        button.disabled = false;
      } catch (error) {
        console.error('[custom-watch-showcase] ATC failed:', error);
        button.textContent = this.strings.unavailable;
        setTimeout(() => {
          button.textContent = originalLabel || this.strings.addToCart;
          button.disabled = false;
        }, 2500);
      }
    }

    getCartAddUrl() {
      return window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cart/add.js`
        : '/cart/add.js';
    }

    getCartAddPayload(items) {
      const payload = { items };
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart?.getSectionsToRender) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        payload.sections_url = window.location.pathname;
      }
      return payload;
    }

    async postCartAdd(items) {
      const response = await fetch(this.getCartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(this.getCartAddPayload(items)),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Cart add failed');
      }

      return data;
    }

    async addSimpleToCart(card) {
      if (!card.variantId) {
        throw new Error('Missing product data');
      }

      return this.postCartAdd([
        {
          quantity: 1,
          id: Number(card.variantId),
        },
      ]);
    }

    async addCardToCart(card) {
      if (!card.recipeId || !card.variantId) {
        throw new Error('Missing recipe or product data');
      }

      // Use recipe API (with extended_attributes) — configureApp headless fails on this page
      const recipe = card.recipeData || (await this.fetchRecipe(card.recipeId));
      card.recipeData = recipe;

      let imageUrl = recipe?.images?.view_1 || card.imageEl?.src || '';

      if (imageUrl && !/([?&](width|height)=)/.test(imageUrl)) {
        imageUrl = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=300&height=300`;
      }

      if (!card.componentVariants) {
        card.componentVariants = await this.fetchComponentVariants(card.componentsCollection);
      }

      // Same SKU source as productCustomizer
      const componentSkus = getComponentSkus(recipe);

      const variantsInBundle = (card.componentVariants || []).filter((variant) =>
        Object.values(componentSkus).includes(String(variant.sku))
      );

      const allVariantsAvailable =
        Object.keys(componentSkus).length > 0 &&
        variantsInBundle.length === Object.values(componentSkus).length &&
        variantsInBundle.every((variant) => variant.available);

      if (!allVariantsAvailable) {
        throw new Error('One or more recipe components are unavailable');
      }

      const recipeIdValue = String(recipe.id || card.recipeId);
      const customizeUrl = `${card.productUrl}?recipe=${recipeIdValue}`;

      const selectionProperties = { ...(recipe.custom?.selections || {}) };
      if (selectionProperties.personalization_1line) {
        selectionProperties.personalization_line1 = selectionProperties.personalization_1line;
        delete selectionProperties.personalization_1line;
      }

      // Omit `_time_stamp`: including it drops parent properties on multi-item add
      const items = [
        ...variantsInBundle.map((variant) => ({
          quantity: 1,
          id: Number(variant.id),
          properties: {
            _recipe_id: recipeIdValue,
            _parts_recipe_id: recipeIdValue,
          },
        })),
        {
          quantity: 1,
          id: Number(card.variantId),
          properties: {
            _recipe_id: recipeIdValue,
            _image: imageUrl,
            _url: customizeUrl,
            _resource_url: recipe.uri || '',
            ...selectionProperties,
            ...Object.keys(componentSkus).reduce(
              (obj, key) => ({
                ...obj,
                [`_${key}_SKU`]: componentSkus[key],
              }),
              {}
            ),
          },
        },
      ];

      return this.postCartAdd(items);
    }
  }

  window.customElements.define('custom-watch-showcase', CustomWatchShowcase);
})();
